import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, Globe } from "lucide-react";
import { LOGO } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang, toggle } = useLang();

  const onReserveClick = () => navigate("/nos-packs");
  const reserveLabel = t("nav.reserve");

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/a-propos", label: t("nav.about") },
    { to: "/notre-equipe", label: t("nav.team") },
    { to: "/nos-packs", label: t("nav.packs") },
    { to: "/blog", label: "Blog" },
    { to: "/avis", label: "Avis" },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contact", label: t("nav.contact") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const LangBtn = () => (
    <button data-testid="lang-toggle" onClick={toggle} className="flex items-center gap-1.5 text-sm font-medium text-navy hover:text-gold transition-colors">
      <Globe size={16} /> {lang === "fr" ? "EN" : "FR"}
    </button>
  );

  return (
    <header data-testid="main-navbar" className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-xl shadow-sm py-2" : "bg-white/70 backdrop-blur-md py-3"}`}>
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={LOGO} alt="Work & Travel Expert" className="h-14 w-14 object-contain" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-heading text-xl font-semibold text-navy tracking-tight">Work &amp; Travel Expert</span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-gold font-semibold">Australie &amp; Canada</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to}
              className={`text-sm font-medium transition-colors hover:text-gold ${location.pathname === l.to ? "text-gold" : "text-navy"}`}>
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-5">
          <LangBtn />
          {user ? (
            <button data-testid="nav-espace-client" onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
              className="flex items-center gap-2 text-sm font-medium text-navy hover:text-gold transition-colors">
              <User size={18} /> {user.role === "admin" ? "Admin" : t("nav.client")}
            </button>
          ) : (
            <button data-testid="nav-login-btn" onClick={() => navigate("/acces")}
              className="text-sm font-medium text-navy hover:text-gold transition-colors">{t("nav.client")}</button>
          )}
          <button data-testid="nav-reserve-btn" onClick={onReserveClick}
            className="bg-gold hover:bg-gold-hover text-white text-sm font-medium px-6 py-3 transition-colors">
            {reserveLabel}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-4">
          <LangBtn />
          <button data-testid="nav-mobile-toggle" className="text-navy" onClick={() => setOpen(!open)}>
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-white border-t border-border px-5 py-4 space-y-3">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block text-navy font-medium py-1">{l.label}</Link>
          ))}
          <button onClick={() => navigate(user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/acces")}
            className="block text-navy font-medium py-1 w-full text-left">{t("nav.client")}</button>
          <button data-testid="nav-mobile-reserve-btn" onClick={onReserveClick} className="w-full bg-gold text-white py-3 font-medium mt-2">{reserveLabel}</button>
        </div>
      )}
    </header>
  );
};
